import en from './en.json';
import de from './de.json';
import tr from './tr.json';

export type Locale = 'en' | 'de' | 'tr';

const dictionaries: Record<Locale, Record<string, unknown>> = { en, de, tr };

/**
 * Map an app locale to its BCP-47 tag for `Intl.*` / `toLocaleString` calls.
 * Centralised so adding a future locale is a one-line change here, not a
 * sweep of `locale === 'de' ? 'de-DE' : 'en-US'` ternaries across the app.
 */
const BCP47: Record<Locale, string> = {
  en: 'en-GB',
  de: 'de-DE',
  tr: 'tr-TR',
};

export function toBcp47(locale: Locale): string {
  return BCP47[locale];
}

/**
 * Resolve a dot-notation key against a nested object.
 * Returns `undefined` when the path doesn't exist.
 */
function resolve(obj: Record<string, unknown>, path: string): string | undefined {
  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
  return typeof value === 'string' ? value : undefined;
}

/**
 * Translate a dot-notation key (e.g. `"nav.dashboard"`) for the given locale.
 *
 * - Supports `{placeholder}` interpolation via `params`.
 * - Falls back to English when the key is missing in the target locale.
 * - Returns the raw key if not found in any locale (makes missing keys visible during dev).
 */
export function t(
  key: string,
  locale: Locale,
  params?: Record<string, string | number>,
): string {
  let text = resolve(dictionaries[locale], key) ?? resolve(dictionaries.en, key) ?? key;

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replaceAll(`{${k}}`, String(v));
    }
  }

  return text;
}
